import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../../services/api';
import { getFileUrl } from '../../utils/fileUrl';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const OrganizationView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadOrganization = useCallback(async () => {
    try {
      setLoading(true);
      const response = await organizationAPI.getById(id);
      setRecord(response.data);
    } catch (err) {
      setErrorMessage(t('organization.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  const headerActions = (
    <div className="ad-toolbar-group">
      <button type="button" className="ad-btn" onClick={() => navigate('/organization')}>
        <i className="fas fa-arrow-right"></i>
        {t('organization.view.backToList')}
      </button>
      <button
        type="button"
        className="ad-btn ad-btn-primary"
        onClick={() => navigate(`/organization/edit/${id}`)}
      >
        <i className="fas fa-pen"></i>
        {t('organization.view.edit')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <AdminPage title={t('organization.view.title')}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  if (!record) {
    return (
      <AdminPage title={t('organization.view.title')}>
        <StateBlock
          icon="fa-circle-exclamation"
          title={t('organization.list.messages.noData')}
        />
        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      </AdminPage>
    );
  }

  const logoUrl = getFileUrl(record.logo);

  const items = [
    ['organizationCode', record.organizationCode],
    ['nameAr', record.organizationNameAr],
    ['nameEng', record.organizationNameEng],
    ['crNumber', record.crNumber],
    ['vatNumber', record.vatNumber],
    ['country', record.country?.nameEng],
    ['area', record.area?.areaNameEng],
    ['city', record.city?.cityNameEng],
    ['district', record.district?.districtNameEng],
    ['streetName', record.streetName],
    ['buildingNumber', record.buildingNumber],
    ['additionalBuildingNumber', record.additionalBuildingNumber],
    ['postalCode', record.postalCode],
    ['poBox', record.poBox],
    ['zipCode', record.zipCode],
    ['telephoneNumber', record.telephoneNumber],
    ['mobileNumber', record.mobileNumber],
    ['faxNumber', record.faxNumber],
    ['email', record.email],
    ['website', record.website],
  ];

  return (
    <AdminPage
      title={record.organizationNameAr || t('organization.view.title')}
      subtitle={record.organizationNameEng}
      headerActions={headerActions}
    >
      <div className="ad-detail">
        <div className="ad-file-row" style={{ marginBottom: '24px' }}>
          <span className="ad-file-preview">
            {logoUrl ? <img src={logoUrl} alt="" /> : <i className="fas fa-image"></i>}
          </span>
          <span className="ad-detail-label">
            {logoUrl
              ? t('organization.view.fields.logo')
              : t('organization.view.fields.noLogo')}
          </span>
        </div>

        <div className="ad-detail-grid">
          {items.map(([key, value]) => (
            <div key={key} className="ad-detail-item">
              <span className="ad-detail-label">
                {t(`organization.view.fields.${key}`)}
              </span>
              <span className="ad-detail-value">
                {value === null || value === undefined || value === '' ? '-' : value}
              </span>
            </div>
          ))}

          <div className="ad-detail-item">
            <span className="ad-detail-label">
              {t('organization.view.fields.isActive')}
            </span>
            <span>
              <span
                className={`ad-badge ${record.isActive ? 'ad-badge-success' : 'ad-badge-danger'}`}
              >
                {record.isActive ? t('common.active') : t('common.inactive')}
              </span>
            </span>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default OrganizationView;
